/* global customElements, HTMLElement */
if (!customElements.get('hotspot-module')) {
  customElements.define(
    'hotspot-module',
    class HotspotModule extends HTMLElement {
      static HOVER_OPEN_DELAY = 150; // ms — hover-intent, ignores fly-throughs
      static HOVER_CLOSE_DELAY = 200; // ms — bridge time pin → panel
      static instances = new Set();

      connectedCallback() {
        this._analyticsEnabled = this.dataset.analytics === 'true';
        this._isDesignMode = this.dataset.designMode === 'true';
        this._isTouch = window.matchMedia('(hover: none)').matches;
        this._abortController = new AbortController();

        this._activePin = null;
        this._openMode = null; // 'hover' | 'click'
        this._hoverOpenTimer = null;
        this._hoverCloseTimer = null;
        this._resizeRaf = null;

        this._liveRegion = this.querySelector('[data-hotspot-live]');
        this._pinWraps = Array.from(this.querySelectorAll('.hotspot-module__pin-wrap'));

        this._bindEvents();
        HotspotModule.instances.add(this);
      }

      disconnectedCallback() {
        clearTimeout(this._hoverOpenTimer);
        clearTimeout(this._hoverCloseTimer);
        if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
        this._abortController?.abort();
        HotspotModule.instances.delete(this);
      }

      _bindEvents() {
        const { signal } = this._abortController;

        this._pinWraps.forEach((wrap) => {
          const pin = wrap.querySelector('.hotspot-module__pin');
          if (!pin) return;

          // Click / tap — toggles, persists until clicked again or outside
          pin.addEventListener(
            'click',
            (e) => {
              e.stopPropagation();
              clearTimeout(this._hoverOpenTimer);
              clearTimeout(this._hoverCloseTimer);
              this._toggle(pin, 'click');
            },
            { signal }
          );

          // Keyboard
          pin.addEventListener(
            'keydown',
            (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this._toggle(pin, 'click');
              } else if (e.key === 'Escape') {
                this._closeActive();
                pin.focus();
              }
            },
            { signal }
          );

          // Desktop hover — hover-intent: only opens after sustained hover
          if (!this._isTouch) {
            wrap.addEventListener(
              'mouseenter',
              () => {
                clearTimeout(this._hoverCloseTimer);
                if (this._openMode === 'click') return; // click mode wins
                if (this._activePin === pin) return;
                clearTimeout(this._hoverOpenTimer);
                this._hoverOpenTimer = setTimeout(() => {
                  this._open(pin, 'hover');
                }, HotspotModule.HOVER_OPEN_DELAY);
              },
              { signal }
            );

            wrap.addEventListener(
              'mouseleave',
              () => {
                // If we hadn't yet opened (still within hover-intent delay), abort
                clearTimeout(this._hoverOpenTimer);
                if (this._openMode === 'hover' && this._activePin === pin) {
                  this._hoverCloseTimer = setTimeout(() => {
                    this._close(pin);
                  }, HotspotModule.HOVER_CLOSE_DELAY);
                }
              },
              { signal }
            );
          }
        });

        // Click outside closes the active panel (click mode only)
        document.addEventListener(
          'click',
          (e) => {
            if (!this.contains(e.target) && this._activePin && this._openMode === 'click') {
              this._closeActive();
            }
          },
          { signal }
        );

        // Re-position the active panel on resize / orientation change so it
        // never clips out of the viewport when the user rotates the device or
        // resizes the window with a panel still open.
        const reposition = () => {
          if (!this._activePin) return;
          const panel = this._activePin
            .closest('.hotspot-module__pin-wrap')
            ?.querySelector('.hotspot-module__panel');
          if (panel) this._positionPanel(this._activePin, panel);
        };

        window.addEventListener(
          'resize',
          () => {
            if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
            this._resizeRaf = requestAnimationFrame(reposition);
          },
          { signal }
        );

        window.addEventListener('orientationchange', reposition, { signal });
      }

      _toggle(pin, mode) {
        if (pin.getAttribute('aria-expanded') === 'true') {
          this._close(pin);
        } else {
          this._open(pin, mode);
        }
      }

      _open(pin, mode) {
        // Close active in this instance
        if (this._activePin && this._activePin !== pin) this._close(this._activePin);

        // Close active in sibling instances — only one panel open across the whole page
        HotspotModule.instances.forEach((inst) => {
          if (inst !== this && inst._activePin) inst._closeActive();
        });

        const wrap = pin.closest('.hotspot-module__pin-wrap');
        const panel = wrap?.querySelector('.hotspot-module__panel');
        if (!panel) return;

        pin.setAttribute('aria-expanded', 'true');
        panel.removeAttribute('hidden');
        panel.setAttribute('aria-hidden', 'false');

        // Allow the panel to layout before measuring for smart positioning
        requestAnimationFrame(() => {
          this._positionPanel(pin, panel);
          panel.classList.add('is-open');
        });

        this._activePin = pin;
        this._openMode = mode;
        this._announce(pin.getAttribute('aria-label'));
        this._dispatchAnalytics(pin);
      }

      _close(pin) {
        const wrap = pin.closest('.hotspot-module__pin-wrap');
        const panel = wrap?.querySelector('.hotspot-module__panel');
        if (!panel) return;

        pin.setAttribute('aria-expanded', 'false');
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');

        const onEnd = (e) => {
          if (e.propertyName !== 'opacity') return;
          panel.setAttribute('hidden', '');
          // Reset any pixel shift so the next open starts from a clean state
          panel.style.translate = '';
          panel.removeEventListener('transitionend', onEnd);
        };
        panel.addEventListener('transitionend', onEnd);

        // Safety net — if transitionend never fires (e.g. tab hidden), hide after 600ms
        setTimeout(() => {
          if (!panel.classList.contains('is-open')) {
            panel.setAttribute('hidden', '');
            panel.style.translate = '';
          }
        }, 600);

        if (this._activePin === pin) {
          this._activePin = null;
          this._openMode = null;
        }
      }

      _closeActive() {
        if (this._activePin) this._close(this._activePin);
      }

      /**
       * Pick the corner that gives the most space, set alignment + flip classes,
       * then compute any pixel shift needed so the panel never clips the viewport.
       *
       *  1. Measure rendered panel width (already capped to calc(100vw - 24px) via CSS).
       *  2. Pick horizontal alignment by checking which option fits with 12px gutters.
       *  3. Pick vertical placement by comparing space above vs below the pin.
       *  4. Final fit pass — compute the panel's would-be open-state rect MATHEMATICALLY
       *     from the chosen alignment + the wrap's stable rect + the panel's layout
       *     dimensions. We deliberately do NOT call getBoundingClientRect() on the
       *     panel here — its rect would be polluted by the in-flight open transition
       *     (transform: translate(-50%, 6px) scale(0.97) → translate(-50%, 0) scale(1))
       *     and produce a shift that's a few pixels off, visibly mispositioning the
       *     panel on the first show. Layout-only properties are stable.
       */
      _positionPanel(pin, panel) {
        // Reset any prior pixel shift before recalculating alignment.
        panel.style.translate = '';

        panel.classList.remove(
          'hotspot-module__panel--align-left',
          'hotspot-module__panel--align-right',
          'hotspot-module__panel--align-center',
          'hotspot-module__panel--below'
        );

        const wrap = pin.closest('.hotspot-module__pin-wrap');
        if (!wrap) return;

        const wrapRect = wrap.getBoundingClientRect();
        const panelW = panel.offsetWidth || 283;
        const panelH = panel.offsetHeight || 180;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gutter = 12;
        const pinCenterX = wrapRect.left + wrapRect.width / 2;

        // Horizontal — prefer centred; flip if it would clip
        const halfPanel = panelW / 2;
        const wouldClipLeftIfCentered = pinCenterX - halfPanel < gutter;
        const wouldClipRightIfCentered = pinCenterX + halfPanel > vw - gutter;

        let alignment;
        if (!wouldClipLeftIfCentered && !wouldClipRightIfCentered) {
          alignment = 'center';
        } else if (wouldClipLeftIfCentered) {
          const leftAlignedRight = wrapRect.left + panelW;
          alignment = leftAlignedRight <= vw - gutter ? 'left' : 'center';
        } else {
          const rightAlignedLeft = wrapRect.right - panelW;
          alignment = rightAlignedLeft >= gutter ? 'right' : 'center';
        }
        panel.classList.add(`hotspot-module__panel--align-${alignment}`);

        // Vertical — prefer above; flip below if not enough room AND below has more
        const spaceAbove = wrapRect.top;
        const spaceBelow = vh - wrapRect.bottom;
        const isBelow = spaceAbove < panelH + gutter && spaceBelow > spaceAbove;
        if (isBelow) {
          panel.classList.add('hotspot-module__panel--below');
        }

        // Compute the panel's final (post-transition) viewport rect purely from
        // layout numbers — no getBoundingClientRect on the animating panel. Mirrors
        // the CSS rules: align-left/right anchor to wrapRect edges, align-center to
        // the pin centre; --below flips the vertical anchor from above-pin to
        // below-pin. The CSS uses `calc(100% - 0.8rem)` for the vertical offset.
        const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const offset = 0.8 * remPx;

        let panelLeft;
        if (alignment === 'left') panelLeft = wrapRect.left;
        else if (alignment === 'right') panelLeft = wrapRect.right - panelW;
        else panelLeft = pinCenterX - panelW / 2;
        const panelRight = panelLeft + panelW;

        const panelBottom = isBelow ? wrapRect.bottom - offset + panelH : wrapRect.top + offset;
        const panelTop = panelBottom - panelH;

        let shiftX = 0;
        let shiftY = 0;

        if (panelLeft < gutter) shiftX = gutter - panelLeft;
        else if (panelRight > vw - gutter) shiftX = vw - gutter - panelRight;

        if (panelTop < gutter) shiftY = gutter - panelTop;
        else if (panelBottom > vh - gutter) shiftY = vh - gutter - panelBottom;

        if (shiftX !== 0 || shiftY !== 0) {
          panel.style.translate = `${shiftX}px ${shiftY}px`;
        }
      }

      _announce(label) {
        if (!this._liveRegion || !label) return;
        // Clear first so screen readers re-read repeated panel openings
        this._liveRegion.textContent = '';
        requestAnimationFrame(() => {
          this._liveRegion.textContent = label + ' panel opened';
        });
      }

      _dispatchAnalytics(pin) {
        if (!this._analyticsEnabled) return;
        const detail = {
          sectionId: this.dataset.sectionId,
          pinLabel: pin.getAttribute('aria-label'),
          contentType: pin.closest('[data-content-type]')?.dataset.contentType,
        };
        document.dispatchEvent(
          new CustomEvent('hotspot-module:panel-opened', { bubbles: true, detail })
        );
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({ event: 'hotspot_panel_open', ...detail });
        }
      }
    }
  );
}
